import React, { useState, useEffect } from 'react';
import './Tracking.css';
import { track } from './../analytics.js';

// Lob's tracking_event objects use `type` as a generic category ('normal',
// 'error') and put the actual scan label in `name` ("In Transit",
// "Delivered", "Processed for Delivery", etc.). Just trust `name` for
// display and for delivered-detection -- no enum mapping needed.

// Lob postcard statuses that mean the piece is already with the carrier. The
// earlier lifecycle values ('created', 'rendered', 'processed', 'printed')
// all mean it hasn't been handed off yet.
const MAILED_STATUSES = [
  'mailed',
  'in_transit',
  'in_local_area',
  'processed_for_delivery',
  'delivered',
  're_routed',
  'returned_to_sender',
];

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Build a single reverse-chronological timeline that merges the lifecycle
// milestones Lob gives us as discrete fields (date_created, send_date,
// expected_delivery_date) with the raw USPS scan events. The previous design
// had two separate views (a 6-step status bar and a "Recent activity" event
// log) which could disagree visually.
function buildTimelineItems(data) {
  const items = [];
  const events = data.tracking_events || [];
  // Treat as delivered if either the postcard.status field or any scan event
  // says so -- Lob's status field can lag the events by a day, so relying on
  // status alone leaves "Expected delivery" stuck on the timeline after the
  // postcard has actually arrived. Tracking events identify themselves in
  // `name` (e.g. "Delivered"); `type` is always 'normal'/'error'.
  const isDelivered = data.status === 'delivered'
    || events.some(ev => (ev.name || '').toLowerCase() === 'delivered');

  if (!isDelivered && data.expected_delivery_date) {
    items.push({
      key: 'expected',
      time: data.expected_delivery_date,
      label: 'Expected delivery',
      pending: true,
      dateOnly: true,
    });
  }

  events.forEach((ev, i) => {
    items.push({
      key: ev.id || `ev-${i}-${ev.time}-${ev.name}`,
      time: ev.time,
      label: ev.name || ev.type,
      location: ev.location,
      description: ev.description,
    });
  });

  // Lob assigns send_date at creation -- it's the SCHEDULED handoff to USPS
  // (typically a few hours out), not proof the postcard has shipped. Treating
  // its presence as "Mailed" told every buyer their card was already mailed
  // seconds after ordering. Only call it mailed once Lob's status says so, or
  // once the scheduled date has actually passed; until then it's a pending
  // step like "Expected delivery".
  if (data.send_date) {
    const hasMailed = MAILED_STATUSES.includes(data.status)
      || new Date(data.send_date) <= new Date();
    items.push({
      key: 'mailed',
      time: data.send_date,
      label: hasMailed ? 'Mailed' : 'Scheduled to mail',
      pending: !hasMailed,
    });
  }

  if (data.date_created) {
    items.push({
      key: 'order_placed',
      time: data.date_created,
      label: 'Order placed',
    });
  }

  // Forward-chronological: oldest at top, newest (or pending "Expected
   // delivery") at the bottom -- reads like a journey, not a news feed.
  items.sort((a, b) => new Date(a.time) - new Date(b.time));

  // Collapse consecutive runs of the same scan name. USPS scans a postcard at
  // every sort facility it touches, so the raw event log often has multiple
  // "In Transit" entries minutes or hours apart -- it looks redundant in the
  // UI even though each row is technically a real distinct scan. Only the
  // first of each run is kept so the timeline reads as state changes. If the
  // postcard later re-enters "In Transit" after a different scan (e.g. after
  // Re-routed), that second run still shows.
  const deduped = [];
  for (const item of items) {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.label && item.label && prev.label.toLowerCase() === item.label.toLowerCase()) {
      continue;
    }
    deduped.push(item);
  }
  return deduped;
}

function DeliveryConfirmation({ postcardId, initialConfirmation }) {
  const [confirmation, setConfirmation] = useState(initialConfirmation || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = (status) => {
    setSaving(true);
    setError(null);
    const prev = confirmation;
    // Optimistic update so the buttons feel responsive.
    setConfirmation(status === 'pending' ? null : { status, timestamp: new Date().toISOString() });

    fetch(process.env.REACT_APP_BACKEND_URL + '/delivery-confirmation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN,
      },
      body: JSON.stringify({ postcardId, status }),
      mode: 'cors',
      credentials: 'same-origin',
      cache: 'no-cache',
    })
      .then(res => res.json())
      .then(json => {
        if (json.result === 'error') throw new Error(json.message || 'Save failed');
        track('delivery_confirmation_submitted', { postcard_id: postcardId, status });
      })
      .catch(e => {
        // Roll back on failure.
        setConfirmation(prev);
        setError("Couldn't save your answer. Try again in a moment.");
      })
      .finally(() => setSaving(false));
  };

  const current = confirmation && confirmation.status;

  return (
    <div className="delivery-confirmation">
      <h2>Did this postcard actually arrive?</h2>
      <p className="delivery-confirmation-help">
        USPS marked this as delivered &mdash; tell us if it really made it.
      </p>

      {!current && (
        <div className="delivery-confirmation-buttons">
          <button
            type="button"
            className="delivery-btn delivery-btn-yes"
            disabled={saving}
            onClick={() => submit('confirmed_delivered')}
          >
            Yes, it arrived
          </button>
          <button
            type="button"
            className="delivery-btn delivery-btn-no"
            disabled={saving}
            onClick={() => submit('didnt_arrive')}
          >
            No, it didn't
          </button>
        </div>
      )}

      {current === 'confirmed_delivered' && (
        <p className="delivery-confirmation-answer">
          <span className="delivery-confirmation-tag is-yes">Confirmed delivered</span>
          {' '}&middot;{' '}
          <button type="button" className="link-button" disabled={saving} onClick={() => submit('pending')}>
            Change my answer
          </button>
        </p>
      )}

      {current === 'didnt_arrive' && (
        <p className="delivery-confirmation-answer">
          <span className="delivery-confirmation-tag is-no">Marked as not arrived</span>
          {' '}&middot;{' '}
          <button type="button" className="link-button" disabled={saving} onClick={() => submit('pending')}>
            Change my answer
          </button>
        </p>
      )}

      {error && <p className="delivery-confirmation-error">{error}</p>}
    </div>
  );
}

export default function Tracking({ postcardId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    track('tracking_viewed', { postcard_id: postcardId });
    let cancelled = false;
    fetch(process.env.REACT_APP_BACKEND_URL + '/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN,
      },
      body: JSON.stringify({ postcardId }),
      mode: 'cors',
      credentials: 'same-origin',
      cache: 'no-cache',
    })
      .then(res => res.json())
      .then(json => {
        if (cancelled) return;
        if (json.result === 'error') {
          setError(json.message || 'Could not load tracking info.');
        } else {
          setData(json);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not reach the tracking service. Try again in a moment.');
      });
    return () => { cancelled = true; };
  }, [postcardId]);

  if (error) {
    return (
      <div className="App tracking">
        <h1>Postcard tracking</h1>
        <p className="description">{error}</p>
        <p className="description">Reference: <code>{postcardId}</code></p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="App tracking">
        <h1>Postcard tracking</h1>
        <p className="description">Loading tracking info for <code>{postcardId}</code>&hellip;</p>
      </div>
    );
  }

  const items = buildTimelineItems(data);
  const recipient = data.recipient || {};
  const recipientLine = [recipient.city, recipient.state].filter(Boolean).join(', ');
  // Match the same "delivered" check buildTimelineItems uses so the toggle
  // appears precisely when the timeline reaches its final state.
  const events = data.tracking_events || [];
  const isDelivered = data.status === 'delivered'
    || events.some(ev => (ev.name || '').toLowerCase() === 'delivered');

  return (
    <div className="App tracking">
      <h1>Postcard tracking</h1>

      {data.thumbnail && (
        <div className="tracking-thumbnail">
          <img src={data.thumbnail} alt="Postcard preview" />
        </div>
      )}

      <dl className="tracking-meta">
        {recipient.name && (
          <>
            <dt>Recipient</dt>
            <dd>
              {recipient.name}
              {recipientLine && <> &middot; {recipientLine}</>}
              {recipient.country && recipient.country !== 'UNITED STATES' && <> &middot; {recipient.country}</>}
            </dd>
          </>
        )}
        {data.carrier && (
          <>
            <dt>Carrier</dt>
            <dd>{data.carrier}</dd>
          </>
        )}
        <dt>Reference</dt>
        <dd><code>{data.id}</code></dd>
      </dl>

      {items.length > 0 && (
        <ol className="tracking-timeline">
          {items.map(item => (
            <li key={item.key} className={item.pending ? 'is-pending' : ''}>
              <div className="tracking-timeline-time">
                {item.dateOnly ? formatDate(item.time) : formatDateTime(item.time)}
              </div>
              <div className="tracking-timeline-name">{item.label}</div>
              {item.location && <div className="tracking-timeline-loc">{item.location}</div>}
              {item.description && <div className="tracking-timeline-desc">{item.description}</div>}
            </li>
          ))}
        </ol>
      )}

      {isDelivered && (
        <DeliveryConfirmation
          postcardId={data.id}
          initialConfirmation={data.delivery_confirmation}
        />
      )}
    </div>
  );
}
